import { useEffect, useState } from 'react';
import DataTable from '@/components/DataTable.jsx';
import { api, ApiError } from '@/lib/api.js';
import GwBreakdownModal from './GwBreakdownModal.jsx';

const buildColumns = (openBreakdown) => [
	{ key: 'season', label: 'Season', title: 'Season' },
	{
		key: 'web_name',
		label: 'Player',
		title: 'Player name',
		strong: true,
		/* Each row is a different season, so the breakdown uses the row's
		   own season rather than any page-level selection. */
		render: (name, row) => (
			<button
				type="button"
				className="link-button"
				onClick={() => openBreakdown({ season: row.season, id: row.id, name })}
			>
				{name}
			</button>
		),
	},
	{ key: 'team', label: 'Team', title: 'Team' },
	{
		key: 'position',
		label: 'Pos',
		title: 'Position',
		/* Same GKP/DEF/MID/FWD colours as the pitch grid. */
		tone: (value) => value,
	},
	{
		key: 'points',
		label: 'Points',
		title: 'Total points',
		numeric: true,
		defaultSort: 'desc',
	},
	{ key: 'goals', label: 'G', title: 'Goals scored', numeric: true },
	{ key: 'assists', label: 'A', title: 'Assists', numeric: true },
	{ key: 'clean_sheets', label: 'CS', title: 'Clean sheets', numeric: true },
	{ key: 'bonus', label: 'Bonus', title: 'Bonus points', numeric: true },
	{ key: 'minutes', label: 'Mins', title: 'Minutes played', numeric: true },
	{
		key: 'now_cost',
		label: 'Cost',
		title: 'Player cost',
		numeric: true,
		render: (v) => `£${v}`,
	},
	{
		key: 'selected_by_percent',
		label: 'TSB',
		title: 'Teams selected by %',
		numeric: true,
		render: (v) => `${v}%`,
	},
];

export default function TopSeasonsTab() {
	const [rows, setRows] = useState([]);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);
	const [breakdown, setBreakdown] = useState(null);

	useEffect(() => {
		let cancelled = false;
		api
			.get('/api/history/topseasons')
			.then((data) => {
				if (cancelled) return;
				setRows(Array.isArray(data) ? data : []);
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load top seasons.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, []);

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading top seasons…</span>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load top seasons.</strong>
				<span>{error}</span>
			</div>
		);
	}

	return (
		<>
			<DataTable
				columns={buildColumns(setBreakdown)}
				rows={rows}
				rowKey={(r) => `${r.season}-${r.id}`}
				filterPlaceholder="Filter by player, team, position or season"
				maxHeight="70vh"
			/>
			{breakdown && (
				<GwBreakdownModal
					kind="player"
					season={breakdown.season}
					id={breakdown.id}
					name={breakdown.name}
					onClose={() => setBreakdown(null)}
				/>
			)}
		</>
	);
}
